import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { authedFetch } from "./lib/api";

interface Member {
  email: string;
  created_at: string;
  invited_by: string;
}

export function PeopleManager() {
  const [members, setMembers] = useState<Member[]>([]);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("Loading family access…");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const response = await authedFetch("/api/admin/members");
    if (!response.ok) throw new Error("Could not load approved people");
    const next = (await response.json()) as Member[];
    setMembers(next);
    setStatus(next.length ? "" : "No family members have been added yet.");
  }, []);

  useEffect(() => {
    void refresh().catch((error: unknown) => {
      setStatus(error instanceof Error ? error.message : "Could not load approved people");
    });
  }, [refresh]);

  async function invite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setStatus("");
    try {
      const response = await authedFetch("/api/admin/members", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { detail?: string } | null;
        throw new Error(body?.detail ?? "Could not add this person");
      }
      setEmail("");
      await refresh();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Could not add this person");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(member: Member) {
    setBusy(true);
    setStatus("");
    try {
      const response = await authedFetch(`/api/admin/members/${encodeURIComponent(member.email)}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Could not remove this person");
      await refresh();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Could not remove this person");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="people-panel" aria-labelledby="people-heading">
      <div className="library-heading">
        <div>
          <p className="eyebrow">Guest list</p>
          <h2 id="people-heading">People</h2>
        </div>
        <span>{members.length} family {members.length === 1 ? "member" : "members"}</span>
      </div>

      <form className="people-form" onSubmit={(event) => void invite(event)}>
        <label>
          <span>Google account email</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="family@example.com"
            required
          />
        </label>
        <button type="submit" disabled={busy}>Add person</button>
      </form>

      {status && <p className="library-status">{status}</p>}
      {members.length > 0 && (
        <div className="people-list">
          {members.map((member) => (
            <article key={member.email}>
              <div>
                <strong>{member.email}</strong>
                <small>Added {new Date(member.created_at).toLocaleDateString()}</small>
              </div>
              <button type="button" disabled={busy} onClick={() => void revoke(member)}>
                Remove
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
